import { HorizonScanningData } from '@/data/horizonScanningTypes';

export const horizonScanningData: HorizonScanningData = {
  metadata: {
    title: 'Amendment Proposal: Ref. AN 4/1.1.60-25/79',
    status: 'Open for Comments',
    deadline: '12 February 2026',
    subject: 'Proposals for the amendment of Annex 14, Vol I & II; Annex 6 (Parts I, II, III); Annex 9; PANS-ABC; PANS-Aerodromes and PANS-AIM',
    origin: 'Aerodrome Design and Operations Panel (ADOP/5)',
    description: 'Consideration of proposals related to ground handling, runway surface conditions, aerodrome emergency planning, RPAS integration, and disabled aircraft removal',
    reference: 'AN 4/1.1.60-25/79'
  },
  items: [
    {
      id: '1',
      section: 'Section 1.1 Definitions (RPAS)',
      source: 'Annex 14, Vol I',
      excerpt: 'Introduction of definitions for C2 Link, Remote Pilot, Remote Pilot Station (RPS), and Remotely Piloted Aircraft System (RPAS).',
      rationale: 'Definitions added to provide better understanding of terms related to RPAS for consistency with Annex 1, 2, and 8.',
      regulatoryImpact: {
        file: 'EASA CS-ADR-DSN',
        section: 'Book 1 - General',
        assessment: 'Current EASA certification specifications for Aerodrome Design (CS-ADR-DSN) do not currently distinguish between "Pilot" and "Remote Pilot."',
        excerptMatch: 'EU Reg 139/2014 Article 2 (Definitions) will require updates to mirror ICAO\'s distinction between manned and unmanned taxiing operations.'
      },
      recommendations: {
        priorityScore: 95,
        priorityLevel: 'High',
        recommendation: [
          'Update Aerodrome Manual "Definitions" section.',
          'Initiate a compatibility study for "Remote Pilot" vs "Pilot" responsibilities in the Aerodrome Emergency Plan.',
          'Review Master Plan for RPAS integration zones.'
        ],
        action: 'Review Master Plan for RPAS integration zones.'
      },
      domain: 'Safety'
    },
    {
      id: '2',
      section: 'Section 9.7 Ground Handling',
      source: 'Annex 14, Vol I',
      excerpt: 'States should ensure that ground handling-related safety requirements take into account the occupational health and safety of ground handling personnel.',
      rationale: 'Ensures no conflicts between aviation regulations and occupational health safety (OHS).',
      regulatoryImpact: {
        file: 'EASA Part-ADR.OPS',
        section: 'ADR.OPS.B.005',
        assessment: 'Current EU ground handling regulations focus heavily on operational safety (aircraft damage) but have limited scope regarding OHS integration within the Aerodrome manual structure.',
        excerptMatch: 'Directive 89/391/EEC (OHS Framework) intersects here.'
      },
      recommendations: {
        priorityScore: 80,
        priorityLevel: 'High',
        recommendation: [
          'Audit current Ground Handling Service Provider (GHSP) contracts.',
          'Verify GHSP training records specifically for OHS compliance as per new Note in 9.7.5.',
          'Request OHS alignment documents from all on-site handlers.'
        ],
        action: 'Request OHS alignment documents from all on-site handlers.'
      },
      domain: 'Operations'
    },
    {
      id: '3',
      section: 'Section 10.2.5 Runway Friction (ABAR)',
      source: 'Annex 14, Vol I',
      excerpt: 'Runway surface friction characteristics should also be monitored considering aeroplane braking performance, such as aircraft braking action report (ABAR)... when available.',
      rationale: 'ABAR is introduced as an additional means to augment conventional friction measurement.',
      regulatoryImpact: {
        file: 'EASA CS-ADR-DSN',
        section: 'DSN.M.750',
        assessment: 'EASA currently relies on GRF (Global Reporting Format). Adding ABAR as a recommended practice requires a change in data collection procedures from ATC/Pilots.',
        excerptMatch: 'EASA SIB 2021-12 (Slippery Runways) may need to be cross-referenced.'
      },
      recommendations: {
        priorityScore: 60,
        priorityLevel: 'Medium',
        recommendation: [
          'Assess availability of ABAR data streams from current airline partners.',
          'Update "Runway Safety Team" agenda to discuss ABAR integration.',
          'No immediate capital expense, procedural update only.'
        ],
        action: 'No immediate capital expense, procedural update only.'
      },
      domain: 'Safety'
    },
    {
      id: '4',
      section: 'Section 2.10 Disabled Aircraft Removal',
      source: 'Annex 14, Vol I',
      excerpt: 'The contact information of the office of the aerodrome coordinator... shall be made available, on request, to aircraft operators. (Upgraded to Standard)',
      rationale: 'Ensures coordination prior to an event; upgraded from Recommendation to Standard.',
      regulatoryImpact: {
        file: 'EASA ADR.OPS.B.090',
        section: 'Disabled Aircraft Removal',
        assessment: 'Current EASA rules require a plan, but the ICAO amendment mandates specific contact availability as a Standard.',
        excerptMatch: 'Reg (EU) 139/2014 requires the "plan" to be established.'
      },
      recommendations: {
        priorityScore: 45,
        priorityLevel: 'Low',
        recommendation: [
          'Publish the specific Coordinator Contact Info in the AIP (Aeronautical Information Publication).',
          'Update internal directory for "Aerodrome Coordinator of Operations."'
        ],
        action: 'Update internal directory for "Aerodrome Coordinator of Operations."'
      },
      domain: 'Operations'
    },
    {
      id: '5',
      section: 'Section 5.3 Wildlife Hazard Management',
      source: 'Annex 14, Vol I',
      excerpt: 'Aerodromes should establish a wildlife hazard management plan that includes risk assessment and mitigation measures.',
      rationale: 'Enhanced focus on proactive wildlife management to prevent bird strikes and other wildlife-related incidents.',
      regulatoryImpact: {
        file: 'EASA CS-ADR-DSN',
        section: 'DSN.M.520',
        assessment: 'Current EASA requirements focus on reactive measures; new amendment emphasizes proactive risk assessment and comprehensive management plans.',
        excerptMatch: 'EU Reg 139/2014 Annex II requires wildlife hazard management but lacks detailed risk assessment requirements.'
      },
      recommendations: {
        priorityScore: 70,
        priorityLevel: 'Medium',
        recommendation: [
          'Conduct comprehensive wildlife risk assessment.',
          'Update Wildlife Hazard Management Plan with proactive measures.',
          'Train staff on new risk assessment methodologies.'
        ],
        action: 'Conduct comprehensive wildlife risk assessment.'
      },
      domain: 'Environment'
    },
    {
      id: '6',
      section: 'Section 7.2 Noise Management',
      source: 'Annex 16, Vol I',
      excerpt: 'Introduction of more stringent noise monitoring requirements for aerodromes in environmentally sensitive areas.',
      rationale: 'Addresses growing community concerns about aircraft noise and environmental impact.',
      regulatoryImpact: {
        file: 'EASA Environmental Protection',
        section: 'EP.A.200',
        assessment: 'Current noise monitoring requirements are basic; new amendment introduces continuous monitoring and public reporting obligations.',
        excerptMatch: 'EU Environmental Noise Directive 2002/49/EC alignment required.'
      },
      recommendations: {
        priorityScore: 65,
        priorityLevel: 'Medium',
        recommendation: [
          'Assess current noise monitoring equipment capabilities.',
          'Develop public noise reporting portal.',
          'Update Noise Abatement Procedures.'
        ],
        action: 'Assess current noise monitoring equipment capabilities.'
      },
      domain: 'Environment'
    }
  ]
};